import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast } from "react-toastify";
import CaptchaBox from "../../auth/CaptchaBox.js";
import { useLanguage } from "../../../utils/languageContext";

// ─── Initial Values ──────────────────────────────────────────────────────────
const initialValues = {
  full_name: "",
  email: "",
  phone: "",
  feedback: "",
};

const FeedbackModal = ({ open, handleClose }) => {
  const { t } = useLanguage();
  const [captcha, setCaptcha] = useState("");
  const [mathCaptcha, setMathCaptcha] = useState({ question: "", answer: 0 });

  // ─── Validation Schema (rebuilt on each render so messages are translated) ──
  const validationSchema = Yup.object({
    full_name: Yup.string()
      .matches(/^[a-zA-Z\s]+$/, t("homepage.feedback.nameAlpha"))
      .min(2, t("homepage.feedback.nameMin"))
      .required(t("homepage.feedback.nameRequired")),

    email: Yup.string()
      .email(t("homepage.feedback.emailInvalid"))
      .required(t("homepage.feedback.emailRequired")),

    phone: Yup.string()
      .matches(/^[6-9]\d{9}$/, t("homepage.feedback.phoneInvalid"))
      .required(t("homepage.feedback.phoneRequired")),

    feedback: Yup.string()
      .min(10, t("homepage.feedback.feedbackMin"))
      .required(t("homepage.feedback.feedbackRequired")),
  });

  useEffect(() => {
    if (open) {
      generateMathCaptcha();
      setCaptcha("");
    }
  }, [open]);

  const generateMathCaptcha = () => {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;
    const operators = ["+", "-"];
    const operator = operators[Math.floor(Math.random() * operators.length)];

    let answer;
    if (operator === "+") {
      answer = num1 + num2;
    } else {
      answer = num1 - num2;
      if (answer < 0) {
        answer = num2 - num1;
        setMathCaptcha({ question: `${num2} ${operator} ${num1}`, answer });
        return;
      }
    }

    setMathCaptcha({ question: `${num1} ${operator} ${num2}`, answer });
  };

  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message === "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", handleResizeObserverError);
    return () => window.removeEventListener("error", handleResizeObserverError);
  }, []);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const response = await axios.post("/nodeapi/content/feedback", {
        full_name: values?.full_name,
        email: values?.email,
        phone: values?.phone,
        feedback: values?.feedback,
      });

      if (response?.data?.success) {
        toast.success(response?.data?.message);
        resetForm();
        handleClose();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* 🔥 Title with Close Button */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {t("homepage.feedback.title")}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
            <Form>
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <TextField
                  label={t("homepage.feedback.nameLbl")}
                  name="full_name"
                  fullWidth
                  value={values?.full_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.full_name && Boolean(errors?.full_name)}
                  helperText={touched?.full_name && errors?.full_name}
                />

                <TextField
                  label={t("homepage.feedback.emailLbl")}
                  name="email"
                  type="email"
                  fullWidth
                  value={values?.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.email && Boolean(errors?.email)}
                  helperText={touched?.email && errors?.email}
                />

                <TextField
                  label={t("homepage.feedback.phoneLbl")}
                  name="phone"
                  fullWidth
                  value={values?.phone}
                  onChange={(e) => {
                    const numericOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFieldValue("phone", numericOnly);
                  }}
                  onBlur={handleBlur}
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  error={touched?.phone && Boolean(errors?.phone)}
                  helperText={touched?.phone && errors?.phone}
                />

                <TextField
                  label={t("homepage.feedback.feedbackLbl")}
                  name="feedback"
                  multiline
                  rows={4}
                  fullWidth
                  value={values?.feedback}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched?.feedback && Boolean(errors?.feedback)}
                  helperText={
                    touched?.feedback && errors?.feedback
                      ? errors.feedback
                      : " "
                  }
                />

                <CaptchaBox
                  mathCaptcha={mathCaptcha}
                  captcha={captcha}
                  setCaptcha={setCaptcha}
                  generateMathCaptcha={generateMathCaptcha}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || parseInt(captcha) !== mathCaptcha.answer}
                  sx={{
                    mt: 1,
                    backgroundColor: "#1b6069",
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: "8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#19a9bc",
                      color: "#fff",
                    },
                    "&:disabled": {
                      opacity: 0.7,
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} sx={{ color: "#000" }} />
                  ) : (
                    t("homepage.feedback.submit")
                  )}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
